import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Calendar } from './Calendar';
import { ResponsiveCalendar } from './ResponsiveCalendar';
import { MonthsCalendar } from './MonthsCalendar';
import { YearsCalendar } from './YearsCalendar';
import { Actions } from './index.constants';
import { DateRangeContext } from './index.context';
import { initialState, reducer } from './index.reducer';
import { StyledWrapper } from './index.styles';

export const init = ({ initialSelected, finalSelected }) => {
    return {
        ...initialState,
        selectedInitialDate: initialSelected,
        selectedFinalDate: finalSelected,
    };
};

export const DateRange = ({ initialSelected, finalSelected, onChange, isMobile, mode }) => {
    const [state, dispatch] = React.useReducer(reducer, { initialSelected, finalSelected }, init);

    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (
            initialSelected === state.selectedInitialDate &&
            finalSelected === state.selectedFinalDate
        ) {
            return;
        }

        onChange(state.selectedInitialDate, state.selectedFinalDate);
    }, [onChange, state.selectedInitialDate, state.selectedFinalDate]);

    const renderDaysCalendar = () => (
        <>
            {isMobile ? (
                <>
                    <ResponsiveCalendar
                        onChangeYear={Actions.CHANGE_YEAR}
                        onChangeMonth={Actions.CHANGE_MONTH}
                        onSelect={Actions.SELECT_DAY}
                        date="uniqDate"
                    />
                </>
            ) : (
                <>
                    <Calendar
                        onDecrease={Actions.DECREASE_INITIAL_MONTH}
                        onIncrease={Actions.INCREASE_INITIAL_MONTH}
                        onSelect={Actions.SELECT_DAY}
                        date="initialDate"
                    />
                    <Calendar
                        onDecrease={Actions.DECREASE_FINAL_MONTH}
                        onIncrease={Actions.INCREASE_FINAL_MONTH}
                        onSelect={Actions.SELECT_DAY}
                        date="finalDate"
                    />
                </>
            )}
        </>
    );

    const renderMonthsCalendar = () => (
        <MonthsCalendar
            onChangeYear={Actions.CHANGE_YEAR}
            onChangeMonth={Actions.CHANGE_MONTH}
            onSelect={Actions.SELECT_MONTH}
            date="uniqDate"
        />
    );

    const renderYearsCalendar = () => (
        <YearsCalendar onSelect={Actions.SELECT_YEAR} date="uniqDate" />
    );

    const renderCalendar = () => {
        switch (mode) {
            case 'months':
                return renderMonthsCalendar();

            case 'years':
                return renderYearsCalendar();

            case 'days':
            default:
                return renderDaysCalendar();
        }
    };

    return (
        <StyledWrapper>
            <DateRangeContext.Provider value={{ state, dispatch }}>
                {renderCalendar()}
            </DateRangeContext.Provider>
        </StyledWrapper>
    );
};

DateRange.propTypes = {
    initialSelected: PropTypes.instanceOf(Date),
    finalSelected: PropTypes.instanceOf(Date),
    onChange: PropTypes.func,
    isMobile: PropTypes.bool,
    mode: PropTypes.oneOf(['days', 'months', 'years']),
};

DateRange.defaultProps = {
    initialSelected: null,
    finalSelected: null,
    onChange: () => {},
    isMobile: false,
    mode: 'days',
};

export default DateRange;
